import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Newspaper,
  Users,
  Image as ImageIcon,
  ShieldCheck,
  Activity,
  LogOut,
  ExternalLink,
  Inbox,
  Heart,
  Share2,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Logo from "@/assets/Logo.png";


export default function AdminLayout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    const fetchUnread = () =>
      api.get("/stats").then((r) => setUnread(r.data?.messages_unread || 0)).catch(() => {});
    fetchUnread();
    const t2 = setInterval(fetchUnread, 30000);
    return () => clearInterval(t2);
  }, []);

  const navItem = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-sm transition-colors ${
      isActive
        ? "bg-bordeaux text-beige-light"
        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
    }`;

  const initials = (user?.name || user?.email || "?")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="flex h-screen bg-beige-light overflow-hidden">
      <aside data-testid="admin-sidebar" className="w-64 bg-white border-r border-slate-200 flex flex-col">
        <div className="h-16 px-5 flex items-center gap-3 border-b border-slate-200">
            <span className="w-10 h-10 text-beige-light grid place-items-center font-bold rounded-sm"><img src={Logo} alt="Refah Europa e.V." className="w-full h-full object-contain" /></span>
          <div className="leading-tight">
            <div className="font-semibold text-slate-900">Refah Europa e.V.</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{t("admin.sidebar.subtitle")}</div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1 thin-scroll overflow-y-auto">
          <NavLink to="/admin" end className={navItem} data-testid="sidebar-dashboard">
            <LayoutDashboard size={16} /> {t("admin.sidebar.dashboard")}
          </NavLink>
          <NavLink to="/admin/news" className={navItem} data-testid="sidebar-news">
            <Newspaper size={16} /> {t("admin.sidebar.news")}
          </NavLink>
          <NavLink to="/admin/mitglieder" className={navItem} data-testid="sidebar-members">
            <Users size={16} /> {t("admin.sidebar.members")}
          </NavLink>
          <NavLink to="/admin/galerie" className={navItem} data-testid="sidebar-gallery">
            <ImageIcon size={16} /> {t("admin.sidebar.gallery")}
          </NavLink>
          <NavLink to="/admin/spenden" className={navItem} data-testid="sidebar-donate">
            <Heart size={16} /> {t("admin.sidebar.donate")}
          </NavLink>
          <NavLink to="/admin/social" className={navItem} data-testid="sidebar-social">
            <Share2 size={16} /> {t("admin.sidebar.social")}
          </NavLink>
          <NavLink to="/admin/nachrichten" className={navItem} data-testid="sidebar-messages">
            <Inbox size={16} />
            <span className="flex-1">{t("admin.sidebar.messages")}</span>
            {unread > 0 && (
              <span
                data-testid="sidebar-messages-badge"
                className="text-[10px] font-semibold bg-bordeaux text-beige-light px-1.5 py-0.5 rounded-sm"
              >
                {unread}
              </span>
            )}
          </NavLink>
          <NavLink to="/admin/aktivitaet" className={navItem} data-testid="sidebar-activity">
            <Activity size={16} /> {t("admin.sidebar.activity")}
          </NavLink>
          {user?.role === "super_admin" && (
            <NavLink to="/admin/administratoren" className={navItem} data-testid="sidebar-admins">
              <ShieldCheck size={16} /> {t("admin.sidebar.admins")}
            </NavLink>
          )}
        </nav>

        <div className="border-t border-slate-200 p-3 space-y-3">
          <div className="flex items-center justify-between px-1">
            <Link
              to="/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-xs text-slate-500 hover:text-bordeaux transition-colors"
              data-testid="view-public-site"
            >
              <ExternalLink size={12} /> {t("admin.sidebar.viewSite")}
            </Link>
            <LanguageSwitcher />
          </div>
          <div className="flex items-center gap-3 px-1">
            <Avatar className="w-9 h-9">
              <AvatarFallback className="bg-bordeaux text-beige-light text-xs">{initials}</AvatarFallback>
            </Avatar>
            <div className="leading-tight min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate" data-testid="current-user-name">
                {user?.name || user?.email}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">
                {user?.role === "super_admin" ? t("admin.sidebar.roleSuper") : t("admin.sidebar.roleAdmin")}
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center justify-center gap-2 text-sm font-medium bg-white border border-slate-300 text-slate-700 hover:bg-beige-light rounded-sm px-3 py-2 transition-colors"
          >
            <LogOut size={14} /> {t("admin.sidebar.logout")}
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto thin-scroll">
        <div className="p-6 md:p-10 max-w-7xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
